import React from 'react';
import { Box, Text, Badge } from '@chakra-ui/react';
import { ITableResponse, ICatalogResponse } from '../../../../models/sessions';
import { HeatmapCell } from '../HeatmapCell';

interface AggregateViewProps {
  table: ITableResponse;
  catalog: ICatalogResponse;
}

const DISPERSION_LABEL: Record<string, { label: string; color: string }> = {
  omogen: { label: 'omogen', color: 'green' },
  media: { label: 'media', color: 'orange' },
  alta: { label: 'alta', color: 'red' },
  na: { label: '—', color: 'gray' },
};

const fmtMaybe = (v: number | undefined, decimals = 0): string => {
  if (v === undefined || Number.isNaN(v)) return '—';
  return v.toLocaleString('it-IT', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Modalità AGGREGATE — 1 riga atleta + 3 righe footer Media/Somma/DevStd.
 */
export const AggregateTableView: React.FC<AggregateViewProps> = ({
  table,
  catalog,
}) => {
  const columnDefs = table.meta.columns.map((id) =>
    catalog.metrics.find((m) => m.id === id),
  );

  return (
    <Box overflowX="auto" bg="var(--c-card-bg)">
      <Box
        as="table"
        width="100%"
        style={{ borderCollapse: 'separate', borderSpacing: 0 }}
      >
        <thead>
          <tr style={{ background: 'var(--c-navy-1)', color: 'var(--c-card-bg)' }}>
            <th
              style={{
                padding: '10px',
                textAlign: 'left',
                position: 'sticky',
                left: 0,
                background: '#334155',
                zIndex: 2,
                minWidth: '220px',
              }}
            >
              Atleta
            </th>
            {columnDefs.map((def, i) => (
              <th
                key={def?.id ?? i}
                style={{
                  padding: '8px 10px',
                  textAlign: 'center',
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  minWidth: '90px',
                  whiteSpace: 'nowrap',
                }}
              >
                {def?.label ?? '—'}
                <div style={{ fontSize: '9px', opacity: 0.7, marginTop: 2 }}>
                  {def?.unit}
                  {table.meta.unitMode === 'per_min' && def?.isVolumeMetric
                    ? '/min'
                    : ''}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, idx) => (
            <tr
              key={row.athleteId}
              style={{ background: idx % 2 === 0 ? 'var(--c-row-stripe)' : 'var(--c-card-bg)' }}
            >
              <td
                style={{
                  padding: '10px',
                  fontWeight: 500,
                  position: 'sticky',
                  left: 0,
                  background: idx % 2 === 0 ? 'var(--c-row-stripe)' : 'var(--c-card-bg)',
                  borderRight: '1px solid #e5e7eb',
                  zIndex: 1,
                }}
              >
                <div>{row.athleteName}</div>
                {row.position && (
                  <Text fontSize="xs" color="gray.500">
                    {row.position}
                  </Text>
                )}
              </td>
              {columnDefs.map((def) => (
                <td
                  key={def?.id}
                  style={{
                    padding: '6px 8px',
                    borderBottom: '1px solid #e5e7eb',
                  }}
                >
                  <HeatmapCell
                    value={def ? row.values[def.id] : undefined}
                    strategy={def ? table.meta.heatmap[def.id] : undefined}
                    decimals={def?.decimals ?? 0}
                  />
                </td>
              ))}
            </tr>
          ))}

          {/* Footer · 3 righe sintesi */}
          <tr style={{ background: '#e7f5f1', fontWeight: 700 }}>
            <td
              style={{
                padding: '10px',
                position: 'sticky',
                left: 0,
                background: '#e7f5f1',
                borderTop: '2px solid #0F6E56',
              }}
            >
              Ø Media{' '}
              <Text as="span" fontSize="xs" fontWeight={400} color="gray.600">
                ({table.summary.athletesIncluded} att.)
              </Text>
            </td>
            {columnDefs.map((def) => (
              <td
                key={def?.id}
                style={{
                  padding: '8px',
                  textAlign: 'center',
                  fontFamily: 'JetBrains Mono, monospace',
                  borderTop: '2px solid #0F6E56',
                }}
              >
                {def
                  ? fmtMaybe(
                      table.summary.metrics[def.id]?.mean,
                      def.decimals ?? 0,
                    )
                  : '—'}
              </td>
            ))}
          </tr>

          <tr style={{ background: '#f5ecde', fontWeight: 700 }}>
            <td
              style={{
                padding: '10px',
                position: 'sticky',
                left: 0,
                background: '#f5ecde',
              }}
            >
              Σ Somma
            </td>
            {columnDefs.map((def) => (
              <td
                key={def?.id}
                style={{
                  padding: '8px',
                  textAlign: 'center',
                  fontFamily: 'JetBrains Mono, monospace',
                }}
              >
                {def
                  ? fmtMaybe(
                      table.summary.metrics[def.id]?.sum,
                      def.decimals ?? 0,
                    )
                  : '—'}
              </td>
            ))}
          </tr>

          <tr style={{ background: '#e7e5f0', fontWeight: 700 }}>
            <td
              style={{
                padding: '10px',
                position: 'sticky',
                left: 0,
                background: '#e7e5f0',
              }}
            >
              σ Dev.St
            </td>
            {columnDefs.map((def) => {
              const tag = def
                ? table.dispersion[def.id]
                : undefined;
              const tagInfo = tag
                ? DISPERSION_LABEL[tag]
                : DISPERSION_LABEL.na;
              return (
                <td
                  key={def?.id}
                  style={{
                    padding: '8px',
                    textAlign: 'center',
                    fontFamily: 'JetBrains Mono, monospace',
                  }}
                >
                  {def
                    ? fmtMaybe(
                        table.summary.metrics[def.id]?.stdDev,
                        def.decimals ?? 1,
                      )
                    : '—'}
                  {tag && tag !== 'na' && (
                    <Badge ml={1} colorScheme={tagInfo.color} fontSize="9px">
                      {tagInfo.label}
                    </Badge>
                  )}
                </td>
              );
            })}
          </tr>
        </tbody>
      </Box>
    </Box>
  );
};
